<script setup lang="ts">
import { computed, ref } from 'vue';
import { useMutation, useQuery, useQueryClient } from '@tanstack/vue-query';
import { inventoryApi, type InventoryMovement, type PlaceFilter } from '@/api/inventory';
import { usePermissions } from '@/composables/usePermissions';
import { useResponsiveTable } from '@/composables/useResponsiveTable';
import { apiErrorText } from '@/utils/api-errors';
import { formatMoney, formatQuantity } from '@/utils/inventory';
import ResponsiveDialog from '@/components/responsive-dialog.vue';

// Movements of one place (a patient's home or a warehouse), signed from its
// point of view, with who recorded each one and a confirmed reversal. A
// `shiftId` narrows them to what was recorded in that shift; `readonly` hides
// the reversal.
const props = defineProps<{ place: PlaceFilter & { shiftId?: number }; readonly?: boolean }>();

const { hasPermission } = usePermissions();
const queryClient = useQueryClient();
const canManage = computed(() => !props.readonly && hasPermission.value('inventory', 'manage'));
const { tableProps } = useResponsiveTable({ desktopItemsPerPage: 25 });

const { data: movements, isPending } = useQuery({
  queryKey: ['inventoryMovements', computed(() => props.place)],
  queryFn: () => inventoryApi.movements(props.place),
  initialData: [],
});

const reverseCandidate = ref<InventoryMovement | null>(null);
const error = ref('');

const reverse = useMutation({
  mutationFn: (movement: InventoryMovement) => inventoryApi.reverseMovement(movement.id),
  onSuccess: () => {
    reverseCandidate.value = null;
    error.value = '';
    ['inventoryBalances', 'inventoryUnits', 'inventoryMovements', 'inventoryAlerts']
      .forEach((key) => queryClient.invalidateQueries({ queryKey: [key] }));
  },
  onError: (failure) => {
    reverseCandidate.value = null;
    error.value = apiErrorText(failure, 'No se pudo revertir el movimiento.');
  },
});

function arrivedHere(movement: InventoryMovement): boolean {
  if (props.place.patientId) return movement.toLocation.patientId === props.place.patientId;

  return movement.toLocation.id === props.place.locationId;
}

// What arrived is +, what left is −.
function signedQuantity(movement: InventoryMovement): string {
  return `${arrivedHere(movement) ? '+' : '−'}${formatQuantity(movement.quantity)}`;
}

function counterpart(movement: InventoryMovement): string {
  return arrivedHere(movement) ? movement.fromLocation.name : movement.toLocation.name;
}

const headers = computed(() => [
  { title: 'Fecha', key: 'occurredOn' },
  { title: 'Movimiento', key: 'humanReason' },
  { title: 'Artículo', key: 'itemName' },
  { title: 'Cantidad', key: 'quantity', align: 'end' as const },
  { title: 'Desde / hacia', key: 'counterpart', sortable: false },
  { title: 'Costo', key: 'totalCostCents', align: 'end' as const },
  { title: 'Registrado por', key: 'recordedBy' },
  ...(canManage.value ? [{ title: '', key: 'actions', sortable: false, align: 'end' as const }] : []),
]);
</script>
<template>
  <div>
    <v-alert
      v-if="error"
      type="error"
      variant="tonal"
      class="mb-2"
      :text="error"
      closable
      @click:close="error = ''"
    />
    <v-data-table
      v-bind="tableProps"
      :header-props="{ class: 'font-weight-bold' }"
      :headers="headers"
      :items="movements"
      :loading="isPending"
      no-data-text="Sin movimientos"
      density="comfortable"
    >
      <template #[`item.occurredOn`]="{ item }">
        {{ item.occurredOn.split('-').reverse().join('-') }}
        <div class="text-caption text-medium-emphasis">
          #{{ item.id }}
        </div>
      </template>
      <template #[`item.humanReason`]="{ item }">
        {{ item.humanReason }}
        <v-chip
          v-if="item.reversesMovementId"
          size="x-small"
          class="ml-1"
        >
          Reversa
        </v-chip>
        <v-chip
          v-else-if="item.reversed"
          size="x-small"
          class="ml-1"
        >
          Revertido
        </v-chip>
        <div
          v-if="item.humanExitCause || item.description"
          class="text-caption text-medium-emphasis"
        >
          {{ [item.humanExitCause, item.description].filter(Boolean).join(' · ') }}
        </div>
      </template>
      <template #[`item.itemName`]="{ item }">
        {{ item.itemName }}
        <span
          v-if="item.unitIdentifier"
          class="text-medium-emphasis"
        >· {{ item.unitIdentifier }}</span>
      </template>
      <template #[`item.quantity`]="{ item }">
        {{ signedQuantity(item) }}
      </template>
      <template #[`item.counterpart`]="{ item }">
        {{ counterpart(item) }}
      </template>
      <template #[`item.totalCostCents`]="{ item }">
        {{ formatMoney(item.totalCostCents, item.currency) }}
        <div
          v-if="item.quantity > 1 && item.totalCostCents > 0"
          class="text-caption text-medium-emphasis"
        >
          {{ formatMoney(Math.round(item.totalCostCents / item.quantity), item.currency) }} c/u
        </div>
      </template>
      <template #[`item.actions`]="{ item }">
        <v-btn
          v-if="!item.reversesMovementId && !item.reversed"
          size="small"
          variant="text"
          @click="reverseCandidate = item"
        >
          Revertir
        </v-btn>
      </template>
    </v-data-table>
    <responsive-dialog
      :model-value="!!reverseCandidate"
      max-width="440"
      :mobile-fullscreen="false"
      title="Revertir movimiento"
      @update:model-value="reverseCandidate = null"
    >
      <p v-if="reverseCandidate">
        Se registrará el movimiento contrario de «{{ reverseCandidate.humanReason }} ·
        {{ reverseCandidate.itemName }}», con su mismo costo. Una reversa no se puede revertir.
      </p>
      <template #actions>
        <v-spacer />
        <v-btn @click="reverseCandidate = null">
          Cancelar
        </v-btn>
        <v-btn
          color="primary"
          variant="flat"
          :loading="reverse.isPending.value"
          @click="reverseCandidate && reverse.mutate(reverseCandidate)"
        >
          Revertir
        </v-btn>
      </template>
    </responsive-dialog>
  </div>
</template>
